import { useMemo } from "react";
import { Shipment, TaskStatus } from "@api/graphql/generated/generated-types";

/**
 * Identity of the parts of a shipment that Change Routing depends on. Background
 * refreshes hand the modal a new `shipment` object on every poll (e.g. re-signed
 * photo URLs); none of those changes should reload or reset the route.
 */
export function getShipmentRoutingKey(
  shipment: Shipment | undefined | null
): string {
  if (!shipment) return "";
  return JSON.stringify([
    shipment.id,
    shipment.shipmentDate,
    shipment.driver?.id,
    (shipment.stops || []).map((stop) => [
      stop.id,
      stop.stopDate,
      stop.completedAt,
    ]),
  ]);
}

/** Keeps the same shipment reference until its routing identity changes. */
export function useStableRoutingShipment(shipment: Shipment): Shipment {
  const routingKey = getShipmentRoutingKey(shipment);
  return useMemo(() => shipment, [routingKey]);
}

type RouteTask = {
  sequenceNumber: number;
  status?: TaskStatus;
  stop: { id: string };
};

/**
 * Stops of the driver's active tasks in saved order. The cache can flip a
 * listed task to COMPLETED (Fleet poll) without removing it from the list.
 */
export function getActiveRouteStops<T extends RouteTask>(
  tasks: ReadonlyArray<T> | undefined
): T["stop"][] {
  return [...(tasks || [])]
    .filter(
      (task) =>
        !task.status ||
        task.status === TaskStatus.Created ||
        task.status === TaskStatus.Started
    )
    .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
    .map((task) => task.stop);
}

/**
 * Takes fresh stop details but keeps the user's unsaved order: stops no
 * longer in `fresh` are dropped, new ones are appended in saved order.
 * Returns `current` untouched when nothing differs.
 */
export function mergeRouteStops<T extends { id: string }>(
  current: T[],
  fresh: ReadonlyArray<T>
): T[] {
  const freshById = new Map(fresh.map((stop) => [stop.id, stop]));
  const keptIds = new Set<string>();
  const kept: T[] = [];
  current.forEach((stop) => {
    const freshStop = freshById.get(stop.id);
    if (!freshStop) return;
    keptIds.add(stop.id);
    kept.push(freshStop);
  });
  const added = fresh.filter((stop) => !keptIds.has(stop.id));
  const merged = [...kept, ...added];
  const isUnchanged =
    merged.length === current.length &&
    merged.every((stop, index) => stop === current[index]);
  return isUnchanged ? current : merged;
}

/**
 * Keeps the optimizer's stop order while following membership changes:
 * ids no longer active are dropped and new ones are appended.
 */
export function mergeRouteStopIds(
  current: string[] | undefined,
  freshIds: string[]
): string[] {
  if (!current) return freshIds;
  const freshSet = new Set(freshIds);
  const kept = current.filter((id) => freshSet.has(id));
  const keptSet = new Set(kept);
  const merged = [...kept, ...freshIds.filter((id) => !keptSet.has(id))];
  const isUnchanged =
    merged.length === current.length &&
    merged.every((id, index) => id === current[index]);
  return isUnchanged ? current : merged;
}
